/**
 * 사진 업로드 준비 시트
 * 흐름: 사진 선택(복수) → 미리보기 + 개별 메타/AI 설정 → 업로드 & 분석 시작
 */
import { useRef, useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SearchableSelect } from './SearchableSelect';
import {
  getSiteLocations,
  getSiteDetailLocations,
  getSiteEquipment,
  getSiteProcessTypes,
  getRecentPhotoContexts,
  createSiteLocation,
  createSiteDetailLocation,
  createSiteEquipment,
  createSiteProcessType,
  updateSiteEquipment,
  type SiteEquipment,
  type SiteDetailLocation,
  type SiteProcessType,
} from '../../../lib/site-metadata';
import { FocusCategorySelector } from './FocusCategorySelector';
import { AnalysisRegionModal } from './analysis-region/AnalysisRegionModal';
import { regionAfterAiToggle } from './analysis-region/region-on-ai-toggle';
import { PhotoViewer } from './PhotoViewer';
import { Button, Card, Modal } from '@safetyhub/ui';
import { equipmentDisplayName, type AnalysisRegion } from '@safetyhub/shared';

const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * 바닥 밀착 패널의 **md 분기판** — `MOBILE_BOTTOM_PANEL_CLASS`(`components/common/SignatureModal.tsx`)와
 * 같은 방법이고 분기점만 `sm` → `md`다. 방법(property마다 base 클래스를 덧칠하지 않고 우회하는 법)과
 * 그 제약의 정본은 그 docblock이다 — 여기 베껴 적지 않는다.
 *
 * 분기점이 다른 이유: 이 시트는 종전 `items-end md:items-center`로 **md 미만 전부**(폰·폴드·좁은 태블릿)
 * 에서 바텀시트였다. sm판을 쓰면 640~767px(폴드 펼침 등)이 가운데 카드로 바뀐다.
 * ⚠️ 클래스를 그 상수에서 문자열 치환으로 «파생»하지 말 것 — Tailwind가 소스의 리터럴만 보고
 * CSS를 만들어, 파생된 `md:` 클래스는 **조용히 무스타일**이 된다.
 * ⚠️ 실제로 바닥에 붙어 보이나는 jsdom이 못 잰다 — 캡처가 소유한다.
 */
const MOBILE_BOTTOM_PANEL_MD_CLASS =
  'self-end -mx-4 -mb-4 min-w-[calc(100%+2rem)] rounded-b-none md:self-center md:mx-0 md:mb-0 md:min-w-0 md:rounded-b-modal';

export interface PendingPhoto {
  file: File;
  preview: string; // URL.createObjectURL
  locationId?: string | null;
  equipmentId?: string | null;
  detailLocationId?: string | null;
  skipAnalysis?: boolean;      // 기본: false (분석함)
  focusCategories?: string[];  // 중점 위험 카테고리 코드 배열
  processType?: string | null; // 사진에 직접 저장되는 공종/공정 (대상 없이도 설정 가능)
  /**
   * 중점 분석 «영역» — 지정하면 **첫 분석부터** 사진 위에 이 사각형을 그려 **중점**으로 보게 한다
   * (없으면 그냥 전체 분석). ⚠️ **「그 안만 보게 한다」가 아니다** — 사진은 통째로 보내고, 범위
   * 안에 위험요인이 없으면 전체 분석 결과로 돌아간다(2026-10-06 마커 전환 + 실측).
   *
   * ⚠️ 카테고리와 달리 **점검 단위 폴백이 없다** — 영역은 사진마다 피사체 위치가 다른 고유
   * 속성이라, 한 사진의 좌표를 다른 사진에 적용하면 엉뚱한 자리를 강조한다(결정 2026-10-05).
   * 그래서 일괄 적용 바에도 넣지 않았다.
   */
  analysisRegion?: AnalysisRegion | null;
}

export interface PhotoUploadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  pendingPhotos: PendingPhoto[];
  onPendingPhotosChange: (photos: PendingPhoto[]) => void;
  onConfirmUpload: (photos: PendingPhoto[]) => void;
  onAddMoreFiles: () => void;
  onOpenCamera?: () => void;
  isUploading?: boolean;
  siteId: string;
  companyId: string;
  isDesktop?: boolean;
}

// ---- 설비 메타 인라인 편집 ----

interface EquipmentMetaInlineProps {
  equipment: SiteEquipment;
  companyId: string;
  siteId: string;
  onUpdated: (updated: SiteEquipment) => void;
}

function EquipmentMetaInline({
  equipment,
  companyId,
  siteId,
  onUpdated,
}: EquipmentMetaInlineProps) {
  const [managementNumber, setManagementNumber] = useState(equipment.managementNumber ?? '');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 관리번호 신규 등록 여부: 원래 없었고 새로 입력 중인 경우
  const isNewManagementNumber = !equipment.managementNumber && managementNumber.trim().length > 0;

  // equipment가 바뀌면 상태 동기화
  useEffect(() => {
    setManagementNumber(equipment.managementNumber ?? '');
  }, [equipment.id, equipment.managementNumber]);

  const scheduleSave = useCallback(
    (patch: { managementNumber?: string }) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        try {
          const updated = await updateSiteEquipment(companyId, siteId, equipment.id, patch);
          onUpdated(updated);
        } catch {
          // 저장 실패 시 무시 (UI 상태는 유지)
        }
      }, 600);
    },
    [companyId, siteId, equipment.id, onUpdated]
  );

  return (
    <div className="flex-1 min-w-0">
      <label className="block text-[9px] font-medium text-ink-400 mb-0.5">관리번호</label>
      <input
        type="text"
        value={managementNumber}
        onChange={(e) => {
          setManagementNumber(e.target.value);
          scheduleSave({ managementNumber: e.target.value || undefined });
        }}
        placeholder="없음"
        className="w-full px-1.5 py-1 text-[11px] border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white"
      />
      {isNewManagementNumber && (
        <p className="text-[9px] text-brand-500 mt-0.5">
          "{equipment.name}"에 관리번호 "{managementNumber.trim()}"을 등록합니다
        </p>
      )}
    </div>
  );
}

// ---- 간략한 AI ON/OFF 토글 ----

interface AiToggleProps {
  value: boolean; // true = 분석함
  onChange: (v: boolean) => void;
}

function AiToggle({ value, onChange }: AiToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 ${
        value ? 'bg-brand-600' : 'bg-ink-300'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          value ? 'translate-x-4' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ---- AI 분석 설정 줄 (토글 + 중점 카테고리 + 분석 범위) ----

interface AiAnalysisRowProps {
  photo: PendingPhoto;
  index: number;
  onUpdate: (index: number, patch: Partial<PendingPhoto>) => void;
  onOpenRegion: () => void;
  companyId: string;
  siteId: string;
  sheetMode?: boolean;
  /** 데스크톱 블록만 `flex-shrink-0`을 더한다(absolute inset-0 안에서 높이를 양보하지 않도록). */
  rowClassName?: string;
}

/**
 * 모바일 블록과 데스크톱 블록이 **같은 줄을 두 벌로 그리고 있었다**(썸네일 레이아웃만 다르다).
 * 분석 범위 버튼을 양쪽에 손으로 복사하면 셋째 벌이 되고, 그러면 한쪽만 고치는 날이 온다 —
 * 이 레포가 반복해 겪은 「같은 표시 개념이 여러 surface에 분산」이 그것이다. 그래서 줄 자체를
 * 하나로 모으고 레이아웃 차이만 `rowClassName`으로 받는다.
 */
function AiAnalysisRow({
  photo,
  index,
  onUpdate,
  onOpenRegion,
  companyId,
  siteId,
  sheetMode = false,
  rowClassName = '',
}: AiAnalysisRowProps) {
  const aiOn = photo.skipAnalysis !== true;
  const hasRegion = photo.analysisRegion != null;

  return (
    <div className={`flex items-center gap-2 flex-wrap pt-1.5 ${rowClassName}`}>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-xs font-medium text-ink-500">위험요인 분석</span>
        <AiToggle
          value={aiOn}
          onChange={(v) =>
            onUpdate(index, {
              skipAnalysis: !v,
              focusCategories: v ? photo.focusCategories : [],
              // 분석을 끄면 범위도 함께 버린다 — 분석하지 않는 사진에 범위가 남아 있으면
              // 다시 켰을 때 사용자가 지정한 기억이 없는 범위로 분석이 돈다.
              // 같은 규칙을 「전체 적용」 바도 쓴다 — 규칙은 `regionAfterAiToggle` 한 벌이 소유한다.
              analysisRegion: regionAfterAiToggle(v, photo.analysisRegion),
            })
          }
        />
      </div>
      {aiOn && (
        <>
          {/*
            ⚠️ `min-w-0`이 아니라 **`min-w-min`**(= `min-width: min-content`)이다.
            바꾸면 「범위 지정」 버튼이 가려진다 — 2026-10-05에 실제로 그랬다.

            `min-w-0`이면 이 칸은 **0까지 줄어드는데** 안의 트리거는 `min-w-[120px]`
            (`FocusCategorySelector` 데스크톱 갈래)라 더는 줄지 않아 **제 칸을 넘친다.**
            이 줄은 `sm:w-[260px]` 열 안에 있어 한 줄에 다 들어갈 수가 없고
            (좌측 토글 묶음 ~115 + 트리거 120 + 버튼 ~62 + gap 16 = 313 > 260),
            넘친 트리거는 **`relative` 부모를 타고 정적인 버튼 위에 그려진다**
            (positioned가 static보다 위다) → 버튼이 자기 중심의 클릭을 못 받는다.
            이 줄은 이미 `flex-wrap`이므로, 칸이 「내용이 실제로 필요한 만큼」을 요구하게
            해 주면 **겹치는 대신 버튼이 다음 줄로 내려간다.**

            왜 `min-w-[120px]`로 복사하지 않나: 120은 트리거가 소유한 값이라 여기 적어 두면
            그쪽이 바뀔 때 조용히 어긋난다. `min-content`는 그 값을 **파생**한다 — 데스크톱
            갈래에서는 정확히 120px로 계산되고(라벨은 `overflow-hidden` flex item 안이라
            automatic minimum이 0이므로 긴 라벨이 이 값을 키우지 않는다),
            phone 갈래(`min-width` 없는 `whitespace-nowrap` 버튼)에서는 그 버튼이 실제로
            필요한 폭이 된다 — 그쪽의 넘침도 같은 한 줄로 막힌다.

            ⚠️ 이 축의 탐지기는 **「잘림」이 아니라 「가려짐」**이다. `scrollWidth > clientWidth`로는
            원리적으로 안 잡힌다(팀장이 그 계측기로 한 번 「정상」으로 오판했다). 재려면
            **버튼 중심의 `document.elementFromPoint`가 그 버튼 자신을 돌려주는가**를 본다.
            jsdom은 레이아웃을 계산하지 않아(`getBoundingClientRect`가 0) **이 축의 spec은
            원리적으로 불가**하다 — 그래서 검사를 두지 않고 이 주석을 둔다. 판정은 브라우저 실측이다.
          */}
          <div className="flex-1 min-w-min">
            <FocusCategorySelector
              selected={photo.focusCategories ?? []}
              onChange={(codes) => onUpdate(index, { focusCategories: codes })}
              layout={sheetMode ? 'phone' : 'desktop'}
              companyId={companyId}
              siteId={siteId}
            />
          </div>
          <button
            type="button"
            onClick={onOpenRegion}
            className={`flex-shrink-0 px-2 py-1 text-xs font-medium rounded-control border transition-colors ${
              hasRegion
                ? 'border-brand-500 text-brand-700 bg-brand-50 hover:bg-brand-100'
                : 'border-line text-ink-600 bg-white hover:bg-surface-hover'
            }`}
            // 지정 여부가 색으로만 구분되면 색을 못 보는 사람에게는 같은 버튼이다 — 문구로도 가른다.
            aria-label={hasRegion ? '분석 범위 변경 (지정됨)' : '분석 범위 지정'}
          >
            {hasRegion ? '범위 지정됨' : '범위 지정'}
          </button>
        </>
      )}
    </div>
  );
}

// ---- 개별 사진 카드 ----

interface PhotoCardProps {
  photo: PendingPhoto;
  index: number;
  locations: Array<{ id: string; name: string; isActive: boolean }>;
  detailLocations: SiteDetailLocation[];
  equipment: SiteEquipment[];
  processTypes: SiteProcessType[];
  companyId: string;
  siteId: string;
  onUpdate: (index: number, patch: Partial<PendingPhoto>) => void;
  onRemove: (index: number) => void;
  onEquipmentCreated: (equip: SiteEquipment) => void;
  onEquipmentUpdated: (equip: SiteEquipment) => void;
  onLocationCreated: (loc: { id: string; name: string; isActive: boolean }) => void;
  onDetailLocationCreated: (loc: SiteDetailLocation) => void;
  onProcessTypeCreated: (pt: SiteProcessType) => void;
  onPreview: (index: number) => void;
  /** 분석 범위 지정 모달 열기 — 모달은 시트가 하나만 들고 있다(카드마다 띄우지 않는다) */
  onOpenRegion: (index: number) => void;
  sheetMode?: boolean;
}

function PhotoCard({
  photo,
  index,
  locations,
  detailLocations,
  equipment,
  processTypes,
  companyId,
  siteId,
  onUpdate,
  onRemove,
  onEquipmentCreated,
  onEquipmentUpdated,
  onLocationCreated,
  onDetailLocationCreated,
  onProcessTypeCreated,
  onPreview,
  onOpenRegion,
  sheetMode = false,
}: PhotoCardProps) {
  const activeLocations = locations.filter((l) => l.isActive);
  const activeDetailLocations = detailLocations.filter((l) => l.isActive);
  const activeEquipment = equipment.filter((e) => e.isActive);
  const selectedEquipment = activeEquipment.find((e) => e.id === photo.equipmentId);
  const equipmentOptions = activeEquipment.map((eq) => ({
    id: eq.id,
    // 하위 설비는 「부모 › 이름」(⑥ ⓑ) · 이름 짓기는 shared 한 벌(설비 대상 라벨과 같은 함수)
    name: equipmentDisplayName(eq),
  }));

  const queryClient = useQueryClient();

  const handleAddLocation = async (name: string): Promise<string> => {
    const created = await createSiteLocation(companyId, siteId, { name });
    onLocationCreated({ id: created.id, name: created.name, isActive: created.isActive });
    await queryClient.invalidateQueries({ queryKey: ['site-locations', companyId, siteId] });
    return created.id;
  };

  const handleAddDetailLocation = async (name: string): Promise<string> => {
    const created = await createSiteDetailLocation(companyId, siteId, { name });
    onDetailLocationCreated(created);
    await queryClient.invalidateQueries({ queryKey: ['site-detail-locations', companyId, siteId] });
    return created.id;
  };

  const handleAddEquipment = async (name: string): Promise<string> => {
    const created = await createSiteEquipment(companyId, siteId, { name });
    onEquipmentCreated(created);
    await queryClient.invalidateQueries({ queryKey: ['site-equipment', companyId, siteId] });
    return created.id;
  };

  return (
    <Card surface="soft" elevation="none" padding="sm" className="relative">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* 좌: 썸네일 + AI (absolute로 우측 높이에 종속) */}
        <div className="sm:w-[260px] sm:flex-shrink-0 sm:self-stretch relative">
          {/* 모바일: 일반 플로우 */}
          <div className="sm:hidden">
            <button type="button" onClick={() => onPreview(index)} className="block w-full focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-card-sm overflow-hidden" aria-label={`사진 ${index + 1} 크게 보기`}>
              <img src={photo.preview} alt={`사진 ${index + 1}`} className="w-full h-[150px] object-cover rounded-card-sm cursor-zoom-in" />
            </button>
            <AiAnalysisRow
              photo={photo}
              index={index}
              onUpdate={onUpdate}
              onOpenRegion={() => onOpenRegion(index)}
              companyId={companyId}
              siteId={siteId}
              sheetMode={sheetMode}
            />
          </div>
          {/* 데스크톱: absolute inset-0 → 우측이 높이 결정 */}
          <div className="hidden sm:flex absolute inset-0 flex-col">
            <button type="button" onClick={() => onPreview(index)} className="block w-full flex-1 min-h-0 overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-card-sm" aria-label={`사진 ${index + 1} 크게 보기`}>
              <img src={photo.preview} alt={`사진 ${index + 1}`} className="w-full h-full object-cover rounded-card-sm cursor-zoom-in" />
            </button>
            <AiAnalysisRow
              photo={photo}
              index={index}
              onUpdate={onUpdate}
              onOpenRegion={() => onOpenRegion(index)}
              companyId={companyId}
              siteId={siteId}
              sheetMode={sheetMode}
              rowClassName="flex-shrink-0"
            />
          </div>
        </div>

        {/* 우: 메타 정보 — 공종 → 대상+관리번호 → 장소 → 상세위치 */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          {/* 대상 + 관리번호 (한 행) */}
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <SearchableSelect
                label="대상"
                value={photo.equipmentId ?? ''}
                onChange={(v) => {
                  const patch: Partial<PendingPhoto> = { equipmentId: v || null };
                  if (v) {
                    const selected = activeEquipment.find((e) => e.id === v);
                    if (selected?.currentLocationId) patch.locationId = selected.currentLocationId;
                    if (selected?.currentDetailLocationId) patch.detailLocationId = selected.currentDetailLocationId;
                  }
                  onUpdate(index, patch);
                }}
                options={equipmentOptions}
                placeholder="대상 선택"
                onAdd={handleAddEquipment}
                sheetMode={sheetMode}
              />
            </div>
            <div className="flex-1 min-w-0">
              {selectedEquipment ? (
                <EquipmentMetaInline
                  equipment={selectedEquipment}
                  companyId={companyId}
                  siteId={siteId}
                  onUpdated={onEquipmentUpdated}
                />
              ) : (
                <div className="opacity-40 pointer-events-none">
                  <label className="block text-[9px] font-medium text-ink-400 mb-0.5">관리번호</label>
                  <input
                    type="text"
                    disabled
                    placeholder="없음"
                    className="w-full px-1.5 py-1 text-[11px] border border-line rounded-control bg-white"
                  />
                </div>
              )}
            </div>
          </div>

          {/* 공종/공정 (항상 표시) */}
          <SearchableSelect
            label="공종/공정"
            value={processTypes.find((pt) => pt.name === (selectedEquipment?.processType ?? photo.processType))?.id ?? ''}
            onChange={(v) => {
              if (!v) {
                onUpdate(index, { processType: null });
                if (selectedEquipment) {
                  const updated = { ...selectedEquipment, processType: null };
                  onEquipmentUpdated(updated);
                  updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: null }).catch(() => {/* 무시 */});
                }
                return;
              }
              const selected = processTypes.find((pt) => pt.id === v);
              if (!selected) return; // onAdd에서 이미 처리됨
              onUpdate(index, { processType: selected.name });
              if (selectedEquipment) {
                const updated = { ...selectedEquipment, processType: selected.name };
                onEquipmentUpdated(updated);
                updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: selected.name }).catch(() => {/* 무시 */});
              }
            }}
            options={processTypes.filter((pt) => pt.isActive).map((pt) => ({ id: pt.id, name: pt.name }))}
            placeholder="공종/공정 선택"
            onAdd={async (name) => {
              const created = await createSiteProcessType(companyId, siteId, { name });
              onProcessTypeCreated(created);
              onUpdate(index, { processType: name });
              if (selectedEquipment) {
                const updated = { ...selectedEquipment, processType: name };
                onEquipmentUpdated(updated);
                updateSiteEquipment(companyId, siteId, selectedEquipment.id, { processType: name }).catch(() => {});
              }
              return created.id;
            }}
            sheetMode={sheetMode}
          />

          {/* 장소 */}
          <SearchableSelect
            label="장소"
            value={photo.locationId ?? ''}
            onChange={(v) => {
              onUpdate(index, { locationId: v || null });
              if (selectedEquipment) {
                const updated = { ...selectedEquipment, currentLocationId: v || null };
                onEquipmentUpdated(updated);
                updateSiteEquipment(companyId, siteId, selectedEquipment.id, { currentLocationId: v || null }).catch(() => {/* 무시 */});
              }
            }}
            options={activeLocations}
            placeholder="장소 선택 (예: 공장A, 창고동, 사무동)"
            onAdd={handleAddLocation}
            sheetMode={sheetMode}
          />

          {/* 상세위치 (항상 활성화) */}
          <SearchableSelect
            label="상세위치"
            value={photo.detailLocationId ?? ''}
            onChange={(v) => {
              onUpdate(index, { detailLocationId: v || null });
              if (selectedEquipment) {
                const updated = { ...selectedEquipment, currentDetailLocationId: v || null };
                onEquipmentUpdated(updated);
                updateSiteEquipment(companyId, siteId, selectedEquipment.id, { currentDetailLocationId: v || null }).catch(() => {/* 무시 */});
              }
            }}
            options={activeDetailLocations}
            placeholder="상세위치 선택 (예: 2층, B라인, 입구)"
            onAdd={handleAddDetailLocation}
            sheetMode={sheetMode}
          />

        </div>
      </div>

      {/* 삭제 버튼: 사진 좌상단 */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-2 left-2 w-6 h-6 bg-ink-800/70 text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors z-10"
        aria-label="사진 제거"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </Card>
  );
}

// ---- 전체 적용 상태 타입 ----

interface BulkState {
  locationId: string;
  aiEnabled: boolean;
  focusCategories: string[];
}

// ---- 메인 컴포넌트 ----

export function PhotoUploadSheet({
  isOpen,
  onClose,
  pendingPhotos,
  onPendingPhotosChange,
  onConfirmUpload,
  onAddMoreFiles,
  onOpenCamera,
  isUploading = false,
  siteId,
  companyId,
  isDesktop = false,
}: PhotoUploadSheetProps) {
  const listEndRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  // 라이트박스 상태
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  /** 분석 범위 모달의 대상 사진 index — 모달은 시트가 **하나만** 들고 있다(카드마다 띄우지 않는다) */
  const [regionIndex, setRegionIndex] = useState<number | null>(null);

  // 전체 적용 상태
  const [bulk, setBulk] = useState<BulkState>({
    locationId: '',
    aiEnabled: true,
    focusCategories: [],
  });

  // 로컬 설비/장소/상세위치/공종 목록 (새로 추가된 항목 반영)
  const [localEquipment, setLocalEquipment] = useState<SiteEquipment[]>([]);
  const [localLocations, setLocalLocations] = useState<Array<{ id: string; name: string; isActive: boolean }>>([]);
  const [localDetailLocations, setLocalDetailLocations] = useState<SiteDetailLocation[]>([]);
  const [localProcessTypes, setLocalProcessTypes] = useState<SiteProcessType[]>([]);

  const { data: locations = [] } = useQuery({
    queryKey: ['site-locations', companyId, siteId],
    queryFn: () => getSiteLocations(companyId, siteId),
    staleTime: 60_000,
    enabled: isOpen,
  });

  const { data: detailLocations = [] } = useQuery({
    queryKey: ['site-detail-locations', companyId, siteId],
    queryFn: () => getSiteDetailLocations(companyId, siteId),
    staleTime: 60_000,
    enabled: isOpen,
  });

  const { data: equipment = [] } = useQuery({
    queryKey: ['site-equipment', companyId, siteId],
    queryFn: () => getSiteEquipment(companyId, siteId),
    staleTime: 60_000,
    enabled: isOpen,
  });

  const { data: processTypes = [] } = useQuery({
    queryKey: ['site-process-types', companyId, siteId],
    queryFn: () => getSiteProcessTypes(companyId, siteId),
    staleTime: 60_000,
    enabled: isOpen,
  });

  const { data: recentContexts = [] } = useQuery({
    queryKey: ['recent-photo-context', companyId, siteId],
    queryFn: () => getRecentPhotoContexts(companyId, siteId, 5),
    staleTime: 30_000,
    enabled: isOpen,
  });

  // 서버 데이터와 로컬 추가 항목 병합
  const mergedLocations = [
    ...locations,
    ...localLocations.filter((ll) => !locations.some((l) => l.id === ll.id)),
  ];
  const mergedDetailLocations = [
    ...detailLocations,
    ...localDetailLocations.filter((ll) => !detailLocations.some((l) => l.id === ll.id)),
  ];
  const mergedEquipment = [
    ...equipment,
    ...localEquipment.filter((le) => !equipment.some((e) => e.id === le.id)),
  ];
  const mergedProcessTypes = [
    ...processTypes,
    ...localProcessTypes.filter((lp) => !processTypes.some((p) => p.id === lp.id)),
  ];

  // 닫힐 때 로컬 상태 초기화 — body 스크롤 잠금은 base Modal이 소유한다(종전 자체 잠금은 걷었다)
  useEffect(() => {
    if (!isOpen) {
      setLocalEquipment([]);
      setLocalLocations([]);
      setLocalDetailLocations([]);
      setLocalProcessTypes([]);
    }
  }, [isOpen]);

  // 사진 추가 시 목록 끝으로 스크롤 — 스크롤 컨테이너(base 본문)가 아니라 끝 표지를 보이게 한다.
  // `?.()`: jsdom에는 `scrollIntoView`가 없다.
  useEffect(() => {
    if (isOpen) listEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [pendingPhotos.length, isOpen]);

  const handleUpdate = useCallback(
    (index: number, patch: Partial<PendingPhoto>) => {
      onPendingPhotosChange(pendingPhotos.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    },
    [pendingPhotos, onPendingPhotosChange]
  );

  const handleRemove = useCallback(
    (index: number) => {
      URL.revokeObjectURL(pendingPhotos[index].preview);
      onPendingPhotosChange(pendingPhotos.filter((_, i) => i !== index));
      // 삭제로 index가 밀리므로 열려 있던 범위 모달을 닫는다 — 안 닫으면 **다른 사진의 범위를
      // 지정하게 된다**(모달 대상이 index라 그 자리가 다음 사진으로 바뀐다).
      setRegionIndex(null);
    },
    [pendingPhotos, onPendingPhotosChange]
  );

  const handleBulkApply = useCallback(() => {
    onPendingPhotosChange(
      pendingPhotos.map((p) => ({
        ...p,
        ...(bulk.locationId ? { locationId: bulk.locationId } : {}),
        skipAnalysis: !bulk.aiEnabled,
        focusCategories: bulk.aiEnabled ? bulk.focusCategories : [],
        // 사진별 토글과 **같은 규칙**이다 — 끄면 범위도 버린다. 이 줄이 빠져 있던 동안에는
        // 끈 사이에 범위가 살아남고 「범위 지정됨」 배지가 숨어 있어, 다시 켜면 사용자가
        // 지정한 기억이 없는 범위로 분석이 돌았다(2026-10-05 리뷰 적발).
        analysisRegion: regionAfterAiToggle(bulk.aiEnabled, p.analysisRegion),
      }))
    );
  }, [pendingPhotos, onPendingPhotosChange, bulk]);

  const handleEquipmentUpdated = useCallback((updated: SiteEquipment) => {
    // 쿼리 캐시 업데이트 (서버에서 가져온 설비의 관리번호 등 반영)
    queryClient.setQueryData(
      ['site-equipment', companyId, siteId],
      (prev: SiteEquipment[] | undefined) => prev?.map((e) => (e.id === updated.id ? updated : e))
    );
    // 로컬 상태도 업데이트 (새로 추가된 설비)
    setLocalEquipment((prev) =>
      prev.map((e) => (e.id === updated.id ? updated : e))
    );
  }, [queryClient, companyId, siteId]);

  if (!isOpen) return null;

  const canUpload = pendingPhotos.length > 0 && !isUploading;
  const activeLocations = mergedLocations.filter((l) => l.isActive);

  // base 푸터(`flex justify-end`) 안에서 종전처럼 가득 차게 `w-full`. 종전 `pb-safe-bottom`은
  // web Tailwind 설정에 정의가 없어 무스타일이었다 — 옮기지 않았다.
  const footer = (
            <div className="flex w-full flex-col sm:flex-row gap-2">
              {/* 사진 추가 버튼 (모바일: 카메라+보관함 2열 grid, sm 이상: 단일 버튼) */}
              <div className={`flex-1 grid gap-2 ${!isDesktop && onOpenCamera ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {!isDesktop && onOpenCamera && (
                  <button
                    type="button"
                    onClick={onOpenCamera}
                    className="flex items-center justify-center gap-2 py-2.5 bg-ink-100 text-ink-700 rounded-control text-sm font-medium active:bg-ink-200 hover:bg-ink-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    촬영하기
                  </button>
                )}
                <button
                  type="button"
                  onClick={onAddMoreFiles}
                  className="flex items-center justify-center gap-2 py-2.5 bg-ink-100 text-ink-700 rounded-control text-sm font-medium active:bg-ink-200 hover:bg-ink-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {isDesktop ? '사진 추가' : IS_IOS ? '사진에서 추가' : '갤러리에서 추가'}
                </button>
              </div>

              {/* 업로드 & 분석 버튼 */}
              {/* 시트 하단 CTA — lg(44px). flex-1로 나란한 버튼과 폭을 나눈다. */}
              <Button
                variant="primary"
                size="lg"
                onClick={() => onConfirmUpload(pendingPhotos)}
                disabled={!canUpload}
                className="flex-1"
              >
                {isUploading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    업로드 중...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    업로드 & 분석 시작 ({pendingPhotos.length}장)
                  </>
                )}
              </Button>
            </div>
  );

  /*
    base Modal이 오버레이·✕·포커스 가둠/복원·스크롤 잠금·`role="dialog"`를 소유한다.

    ⛔ **Esc·바깥 클릭으로 닫지 않는다(종전 동작 그대로 — ✕만 닫는다).** 이 시트를 닫으면
    고른 사진과 사진마다 입력한 장소·설비·공종·분석 설정이 **확인 없이 전부 버려진다**
    (호출부 `onPhotoUploadSheetClose`가 미리보기를 해제하고 목록을 비운다). 그런데:
    - base의 dirty 확인을 쓸 수 없다 — 이 서브트리는 `MODAL_LAYER`(z-[70]~z-[100]) 밖의 z 세계
      (시트 9997 · 사진 뷰어·선택 시트 9998/9999)라 확인 모달이 이 시트 **아래로 숨는다**
      (`AnalysisRegionModal`의 `isDirty` 주석과 같은 판정).
    - 안쪽 선택 시트(`SearchableSelect`)·사진 뷰어(`PhotoViewer`)가 Esc를 **자체 처리하고 전파를
      막지 않아**, base Esc를 켜면 「검색창에서 Esc → 이 시트째 닫힘」이 된다.
    ⚠️ **되짚을 조건**: 이 서브트리가 `MODAL_LAYER`로 통합돼 `isDirty`를 쓸 수 있게 되면 Esc·바깥
    클릭을 dirty 확인과 함께 되살릴 수 있다 — 그때 위 둘째 이유(안쪽 Esc 전파)도 함께 닫을 것.

    모양: 폰·폴드에서는 바닥·좌우에 붙는 시트, **md 이상**에서 가운데 카드다 — 방법과 제약은
    `MOBILE_BOTTOM_PANEL_MD_CLASS` docblock.
    `z-[9997]`은 종전 인라인 값 그대로다(안쪽 9998/9999가 그 위에 떠야 한다). 클래스로 넘기는 것은
    base 레지스트리가 그 클래스에서 층을 읽어 「최상단」을 가르기 때문이다(`AnalysisRegionModal`
    z-[9999]가 위로 판정된다).
  */
  const modal = (
    <>
    <Modal
      open
      onClose={onClose}
      title="사진 업로드"
      size="xl"
      closeOnEsc={false}
      closeOnOverlay={false}
      overlayZClassName="z-[9997]"
      panelClassName={`${MOBILE_BOTTOM_PANEL_MD_CLASS} animate-slide-up md:animate-none`}
      footer={footer}
    >
          {/* 본문 — 스크롤은 base 본문이 소유한다(헤더·푸터 고정) */}
          <div className="space-y-4">
            {/* 전체 적용 (사진 2장 이상) */}
            {pendingPhotos.length > 1 && (
              <div className="sticky -top-1 z-20 bg-brand-50 rounded-card p-3 pt-4 space-y-2.5">
                <p className="text-xs font-semibold text-brand-700">전체 적용 (선택사항)</p>

                {/* 최근 사용 조합 */}
                {recentContexts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {recentContexts.slice(0, 3).map((ctx, idx) => {
                      const label =
                        [ctx.locationName, ctx.equipmentName].filter(Boolean).join(' / ') || '(없음)';
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setBulk((b) => ({
                              ...b,
                              locationId: ctx.locationId ?? '',
                            }));
                            onPendingPhotosChange(
                              pendingPhotos.map((p) => ({
                                ...p,
                                locationId: ctx.locationId,
                                equipmentId: ctx.equipmentId,
                              }))
                            );
                          }}
                          className="px-2.5 py-1 rounded-full text-xs font-medium border bg-white text-ink-700 border-ink-300 hover:border-brand-400 hover:text-brand-600 transition-colors"
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* AI 분석 + 장소 + 적용 */}
                {isDesktop ? (
                  /* 데스크톱: 한 행 */
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-xs font-medium text-ink-500 leading-none">위험요인 분석</span>
                      <AiToggle
                        value={bulk.aiEnabled}
                        onChange={(v) =>
                          setBulk((b) => ({ ...b, aiEnabled: v, focusCategories: v ? b.focusCategories : [] }))
                        }
                      />
                    </div>
                    {bulk.aiEnabled && (
                      <FocusCategorySelector
                        selected={bulk.focusCategories}
                        onChange={(codes) => setBulk((b) => ({ ...b, focusCategories: codes }))}
                        layout="desktop"
                        companyId={companyId}
                        siteId={siteId}
                      />
                    )}
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                      <span className="text-xs font-medium text-ink-500 leading-none">장소</span>
                      <div className="w-28">
                        <SearchableSelect
                          label=""
                          value={bulk.locationId}
                          onChange={(v) => setBulk((b) => ({ ...b, locationId: v }))}
                          options={activeLocations}
                          placeholder="선택"
                          onAdd={async (name) => {
                            const created = await createSiteLocation(companyId, siteId, { name });
                            setLocalLocations((prev) => [...prev, { id: created.id, name: created.name, isActive: created.isActive }]);
                            return created.id;
                          }}
                          sheetMode={!isDesktop}
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={handleBulkApply}
                    >
                      전체 적용
                    </Button>
                  </div>
                ) : (
                  /* 모바일/폴드 */
                  <>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs font-medium text-ink-500 leading-none">위험요인 분석</span>
                        <AiToggle
                          value={bulk.aiEnabled}
                          onChange={(v) =>
                            setBulk((b) => ({ ...b, aiEnabled: v, focusCategories: v ? b.focusCategories : [] }))
                          }
                        />
                      </div>
                      {bulk.aiEnabled && (
                        <div className="flex-1 min-w-0">
                          <FocusCategorySelector
                            selected={bulk.focusCategories}
                            onChange={(codes) => setBulk((b) => ({ ...b, focusCategories: codes }))}
                            layout="phone"
                            companyId={companyId}
                            siteId={siteId}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                        <span className="text-xs font-medium text-ink-500 leading-none">장소</span>
                        <div className="w-24">
                          <SearchableSelect
                            label=""
                            value={bulk.locationId}
                            onChange={(v) => setBulk((b) => ({ ...b, locationId: v }))}
                            options={activeLocations}
                            placeholder="선택"
                            onAdd={async (name) => {
                              const created = await createSiteLocation(companyId, siteId, { name });
                              setLocalLocations((prev) => [...prev, { id: created.id, name: created.name, isActive: created.isActive }]);
                              return created.id;
                            }}
                            sheetMode
                          />
                        </div>
                      </div>
                      {/* 폴드(sm+): 같은 행에 버튼 */}
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="hidden sm:inline-flex flex-shrink-0"
                        onClick={handleBulkApply}
                      >
                        전체 적용
                      </Button>
                    </div>
                    {/* 모바일: 다음 행에 버튼 */}
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="sm:hidden w-full"
                      onClick={handleBulkApply}
                    >
                      전체 적용
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* 사진 목록 */}
            {pendingPhotos.length === 0 ? (
              <div className="text-center py-8 text-ink-400 text-sm">
                사진을 선택해주세요
              </div>
            ) : (
              <div className="space-y-2">
                {pendingPhotos.map((photo, index) => (
                  <PhotoCard
                    key={photo.preview}
                    photo={photo}
                    index={index}
                    locations={mergedLocations}
                    detailLocations={mergedDetailLocations}
                    equipment={mergedEquipment}
                    processTypes={mergedProcessTypes}
                    companyId={companyId}
                    siteId={siteId}
                    onUpdate={handleUpdate}
                    onRemove={handleRemove}
                    onEquipmentCreated={(equip) => setLocalEquipment((prev) => [...prev, equip])}
                    onEquipmentUpdated={handleEquipmentUpdated}
                    onLocationCreated={(loc) => setLocalLocations((prev) => [...prev, loc])}
                    onDetailLocationCreated={(loc) => setLocalDetailLocations((prev) => [...prev, loc])}
                    onProcessTypeCreated={(pt) => setLocalProcessTypes((prev) => [...prev, pt])}
                    onPreview={setLightboxIndex}
                    onOpenRegion={setRegionIndex}
                    sheetMode={!isDesktop}
                  />
                ))}
              </div>
            )}
          </div>
          {/* 사진이 늘면 이 자리로 스크롤한다(위 effect) — 스크롤 컨테이너가 base 본문이라 ref를 걸 수 없다 */}
          <div ref={listEndRef} aria-hidden="true" />
    </Modal>

    {/* 사진 뷰어 라이트박스 */}
    <PhotoViewer
      photos={pendingPhotos.map((p, i) => ({
        id: p.preview,
        url: p.preview,
        caption: p.file.name || `사진 ${i + 1}`,
      }))}
      currentIndex={lightboxIndex ?? 0}
      isOpen={lightboxIndex !== null}
      onClose={() => setLightboxIndex(null)}
      onIndexChange={setLightboxIndex}
    />

    {/* 분석 범위 지정 — 아직 올리지 않은 사진이라 확인은 로컬 상태만 바꾼다(되돌릴 수 있으므로
        경고를 띄우지 않는다. 사진 상세의 재분석 진입은 반대로 경고가 필요하다). */}
    {regionIndex !== null && pendingPhotos[regionIndex] && (
      <AnalysisRegionModal
        isOpen
        onClose={() => setRegionIndex(null)}
        imageUrl={pendingPhotos[regionIndex].preview}
        initialRegion={pendingPhotos[regionIndex].analysisRegion ?? null}
        onConfirm={(region) => {
          handleUpdate(regionIndex, { analysisRegion: region });
          setRegionIndex(null);
        }}
      />
    )}
    </>
  );

  return modal;
}
